import React, { useContext, useEffect, useState } from 'react';
import './index.scss';
import { FaTimes } from 'react-icons/fa';
import { PaymentTypes } from '../../utils/PaymentTypes';
import { SubmitProcessValidationService } from '../SubmitPaymentProcess/SubmitProcessValidationService';
import { APIWebstoreContext } from '../../context/globalContext';
import { getIdpToken } from '../../utils/authHelper';
import { getAPICall, postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { ICP_IDENTIFIER } from '../../utils/constants';

type NonInstantPaymentProps = {
  paymentType: string;
  invoiceIdentifier: string;
};

type UploadFile = {
  fileId: number[];
  name: string;
  size: number;
};

const acceptedFileTypes = [
  'image/bmp',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/tiff',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/pdf',
  'application/rtf',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const MAX_FILE_SIZE_MB = 15;

const BYTES_PER_MB = 1024 * 1024;

const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * BYTES_PER_MB;

const NonInstantPayment = ({
  paymentType,
  invoiceIdentifier,
}: NonInstantPaymentProps): JSX.Element => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>([]);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const { state } = useContext(APIWebstoreContext);
  const [showCheckRemitterNote, setShowCheckRemitterNote] = useState(false);
  const [showACHRemitterNote, setShowACHRemitterNote] = useState(false);
  const [isUpload, setIsUpload] = useState(false);

  const [paymentDetails, setPaymentDetails] = useState({
    bankName: '',
    localBankName: '',
    usBankName: '',
    checkOrMoneyOrderNumber: '',
    remitterName: '',
    paymentDocumentsName: [] as string[],
  });

  const [errors, setErrors] = useState({
    bankName: '',
    localBankName: '',
    usBankName: '',
    checkOrMoneyOrderNumber: '',
    remitterName: '',
  });

  const poNumbers =
    invoiceIdentifier === ICP_IDENTIFIER
      ? [state?.groupInvoiceId?.[invoiceIdentifier]]
      : [...(state?.orderDetails?.ICP?.orderIds ?? [''])];

  useEffect(() => {
    SubmitProcessValidationService.setNonInstantPaymentInfo({}, false);
    fetchUploadedFiles();
  }, []);

  useEffect(() => {
    setShowACHRemitterNote(false);
    setShowCheckRemitterNote(false);
  }, [paymentType]);

  useEffect(() => {
    performGlobalValidation();
  }, [paymentDetails, errors, uploadedFiles]);

  const handleInputChange = (field: keyof typeof paymentDetails, value: string) => {
    setPaymentDetails((prev) => ({ ...prev, [field]: value }));
    performValidations(field, value);
  };

  const performValidations = (field: keyof typeof paymentDetails, value: string) => {
    let error = '';
    if (['bankName', 'remitterName', 'localBankName', 'usBankName'].includes(field)) {
      if (value.length > 100) {
        error = 'Must not exceed 100 characters';
      }
    } else if (field === 'checkOrMoneyOrderNumber' && value.length > 15) {
      error = 'Must not exceed 15 characters';
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const showRemitterNote = () => {
    const isCheck = paymentType === PaymentTypes.Check;
    const isACH = paymentType === PaymentTypes.ACH;

    setShowCheckRemitterNote(isCheck);
    setShowACHRemitterNote(isACH);
  };

  const performGlobalValidation = () => {
    let isValid = false;
    const noErrors = Object.values(errors).every((e) => !e);
    const remitterFilled = paymentDetails.remitterName?.trim() !== '';

    if (paymentType === PaymentTypes.Check) {
      isValid =
        !!paymentDetails.localBankName?.trim() &&
        !!paymentDetails.usBankName?.trim() &&
        !!paymentDetails.checkOrMoneyOrderNumber?.trim() &&
        remitterFilled &&
        noErrors;
    } else if (paymentType === PaymentTypes.WireTransfer) {
      isValid =
        !!paymentDetails.bankName?.trim() && remitterFilled && noErrors && uploadedFiles.length > 0;
    } else {
      isValid = !!paymentDetails.bankName?.trim() && remitterFilled && noErrors;
    }

    const errorMessage =
      state?.sitecoreDictionaryData?.InvalidPaymentDetailsToastMessage ||
      'Please fill-in the required Payment details!';
    SubmitProcessValidationService.setErrorMessage(isValid ? '' : errorMessage);
    SubmitProcessValidationService.setNonInstantPaymentInfo(paymentDetails, isValid);
  };

  const fetchUploadedFiles = async () => {
    try {
      const idpToken = await getIdpToken();
      let allUploadedFiles: any[] = [];

      for (const poNumber of poNumbers) {
        const response = await getAPICall(
          `${process.env.REACT_APP_ICP_URL}/File/GetUploadedInvoiceFiles?PONumber=${poNumber}`,
          {
            headers: {
              Authorization: `Bearer ${idpToken}`,
            },
          }
        );

        allUploadedFiles = [...allUploadedFiles, ...(response?.data || [])];
      }

      const groupedFilesByPO: { [key: string]: UploadFile } = {};

      allUploadedFiles.forEach((file: any) => {
        if (!groupedFilesByPO[file.name]) {
          groupedFilesByPO[file.name] = {
            name: file.name,
            size: file.size,
            fileId: [],
          };
        }

        groupedFilesByPO[file.name].fileId.push(file.id);
      });

      setUploadedFiles(Object.values(groupedFilesByPO));
    } catch (err) {
      console.error('Failed to fetch uploaded files:', err);
      setGlobalError('Failed to fetch uploaded files.');
    }
  };

  const selectFile = async () => {
    setIsUpload(true);
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = acceptedFileTypes.join(',');

    input.onchange = async (e: any) => {
      const files: FileList = e.target.files;
      if (!files) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!acceptedFileTypes.includes(file.type)) {
          const errorMs =
            state?.sitecoreDictionaryData?.InvalidPaymentFileErrorMessage ||
            'File type not allowed. Please retry your upload using one of the supported file formats(.bmp, .jpeg, .jpg, .png, .gif, .tif, .csv, .xls, .xlsx, .doc, .docx, .txt, .pdf, .ppt, .pptx, .rtf, .odt)';
          setGlobalError(errorMs);
          continue;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          setGlobalError(`File too large (max 15MB): ${file.name}`);
          continue;
        }
        const idpToken = await getIdpToken();
        let uploadedFileId: number[] = [];
        for (const poNumber of poNumbers) {
          try {
            const formData = new FormData();
            formData.append('fileUpload', file);
            formData.append('PONumber', poNumber ?? '');

            const response = await postAPICall(
              `${process.env.REACT_APP_ICP_URL}/File/UploadInvoiceFile`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  Authorization: `Bearer ${idpToken}`,
                },
              }
            );

            const fileId = response.data?.id;
            if (fileId) {
              uploadedFileId.push(fileId);
            }
            setGlobalError(null);
          } catch (err) {
            console.error('Upload failed:', err);
            setGlobalError(`Failed to upload file: ${file.name}`);
          }
        }
        if (uploadedFileId.length > 0) {
          setUploadedFiles((prev) => [
            ...prev,
            {
              fileId: uploadedFileId,
              name: file.name,
              size: file.size,
            },
          ]);
        }
      }
      setIsUpload(false);
    };

    window.addEventListener(
      'focus',
      () => {
        setTimeout(() => {
          if (!input.files || input.files.length === 0) {
            setIsUpload(false);
          }
        }, 300);
      },
      { once: true }
    );

    input.click();
  };

  const removeFile = async (fileIds: number[]) => {
    try {
      const idpToken = await getIdpToken();

      await Promise.all(
        fileIds.map((id) =>
          postAPICall(
            `${process.env.REACT_APP_ICP_URL}/File/RemoveUploadedInvoiceFile?fileId=${id}`,
            null,
            {
              headers: {
                Authorization: `Bearer ${idpToken}`,
              },
            }
          )
        )
      );

      setUploadedFiles((prev) => prev.filter((file) => file.fileId !== fileIds));
    } catch (err) {
      console.error('Remove failed:', err);
      setGlobalError('Failed to remove file.');
    }
  };

  return (
    <div className="non-instant-payment">
      <p className={`required-note ${invoiceIdentifier !== ICP_IDENTIFIER ? 'margin-align' : ''}`}>
        <span className="required-star">*</span> Indicates a required field
      </p>

      {paymentType === PaymentTypes.Check ? (
        <>
          <label className="field-label">
            Name of Local Bank <span className="required-star">*</span>
          </label>
          <input
            className="text-input"
            type="text"
            value={paymentDetails.localBankName}
            onChange={(e) => handleInputChange('localBankName', e.target.value)}
            placeholder="Name of Local Bank"
          />
          {errors.localBankName && <p className="error-message">{errors.localBankName}</p>}

          <label className="field-label">
            Name of US Bank <span className="required-star">*</span>
          </label>
          <input
            className="text-input"
            type="text"
            value={paymentDetails.usBankName}
            onChange={(e) => handleInputChange('usBankName', e.target.value)}
            placeholder="Name of US Bank"
          />
          {errors.usBankName && <p className="error-message">{errors.usBankName}</p>}

          <label className="field-label">
            Check / Money Order Number <span className="required-star">*</span>
          </label>
          <input
            className="text-input"
            type="text"
            value={paymentDetails.checkOrMoneyOrderNumber}
            onChange={(e) => handleInputChange('checkOrMoneyOrderNumber', e.target.value)}
            placeholder="Check or Money Order Number"
          />
          {errors.checkOrMoneyOrderNumber && (
            <p className="error-message">{errors.checkOrMoneyOrderNumber}</p>
          )}
        </>
      ) : (
        <>
          <label className="field-label">
            Bank Name <span className="required-star">*</span>
          </label>
          <input
            className="text-input"
            type="text"
            value={paymentDetails.bankName}
            onChange={(e) => handleInputChange('bankName', e.target.value)}
            placeholder="Name"
          />
          {errors.bankName && <p className="error-message">{errors.bankName}</p>}
        </>
      )}

      <label className="field-label">
        Remitter Name <span className="required-star">*</span>
      </label>
      <input
        className="text-input"
        type="text"
        value={paymentDetails.remitterName}
        onChange={(e) => handleInputChange('remitterName', e.target.value)}
        placeholder="Name"
        onFocus={showRemitterNote}
      />
      {invoiceIdentifier !== ICP_IDENTIFIER && showCheckRemitterNote && (
        <p className="error-message error-note">
          {state?.sitecoreDictionaryData && state?.sitecoreDictionaryData?.CheckRemitterNote
            ? state?.sitecoreDictionaryData.CheckRemitterNote
            : 'You must mail your original check or money order, along with a copy of the ICP invoice ' +
              'to the address specified on the invoice. Please be sure to reference the invoice number ' +
              'on your original check.'}
        </p>
      )}
      {invoiceIdentifier !== ICP_IDENTIFIER && showACHRemitterNote && (
        <p className="error-message error-note">
          {state?.sitecoreDictionaryData && state?.sitecoreDictionaryData?.ACHRemitterNote
            ? state?.sitecoreDictionaryData.ACHRemitterNote
            : 'You must complete ACH transactions by physically visiting your bank, and the ICP invoice ' +
              'number must be referenced in your transaction or API will not be able to identify your ' +
              'ACH payment. Please be sure to follow the instructions on the ICP invoice.'}
        </p>
      )}
      {errors.remitterName && <p className="error-message">{errors.remitterName}</p>}

      <label className="field-label">Amount</label>
      <input
        className="text-input read-only"
        type="text"
        value={`$${
          invoiceIdentifier === ICP_IDENTIFIER
            ? Number(state?.invoicesSummary?.[invoiceIdentifier]?.totalAmount || 0).toFixed(2)
            : (state?.orderTotals?.totalAmount || 0).toFixed(2)
        }`}
        readOnly
      />

      <label className="upload-label">
        Upload scanned / digital image of the ACH, Check / Money Order, Wire etc.
        {paymentType === PaymentTypes.WireTransfer ? (
          <span className="required-star">*</span>
        ) : (
          <span className="optional">(optional)</span>
        )}
      </label>

      <div className="upload-container">
        <button type="button" className="upload-button" onClick={selectFile} disabled={isUpload}>
          {isUpload ? 'Uploading...' : 'Select Files…'}
        </button>

        {globalError && <p className="error-message global-error-align">{globalError}</p>}

        {uploadedFiles.length > 0 && (
          <div className="uploaded-files-list">
            {uploadedFiles.map(({ fileId, name, size }) => (
              <div key={fileId[0]} className="file-preview">
                <div className="file-header">
                  <div className="file-name-and-size">
                    <span className="filenamespan">{name}</span>
                    <span className="filesize">({(size / BYTES_PER_MB).toFixed(2)} MB)</span>
                  </div>
                  <FaTimes className="icon facloseicon" onClick={() => removeFile(fileId)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NonInstantPayment;
